import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import Layout from "../components/Layout";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function DsOrderReport() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            try {
                const q = query(
                    collection(db, "Outlet_Orders"),
                    orderBy("createdAt", "desc")
                );
                const snap = await getDocs(q);
                const list = snap.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data(),
                    dateStr: doc.data().createdAt?.toDate ? doc.data().createdAt.toDate().toLocaleString() : "N/A"
                }));
                setOrders(list);
            } catch (err) {
                console.error("Error fetching DS orders:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchOrders();
    }, []);

    const exportDetailedToExcel = () => {
        const rows = [];
        orders.forEach(o => {
            const lines = o.itemLines || [];
            lines.forEach(l => {
                rows.push({
                    "Order ID": o.id,
                    "Order Date": l.orderDate || o.dateStr,
                    "Distributor ID": o.distributorId || "",
                    "Distributor Name": o.distributorName || "",
                    "DS ID": o.dsId || "",
                    "Outlet ID": o.outletId || "",
                    "Outlet Name": o.outletName || "",
                    "Beat": o.beat || "",
                    "SKU Name": l.skuName || "",
                    "HSN": l.hsn || "",
                    "SKU Type": l.skuType || "",
                    "Qty": l.qty || 0,
                    "MRP": l.mrp || 0,
                    "Basic Rate": l.basicRate || 0,
                    "Basic Amount": l.basicAmount || 0,
                    "CGST %": l.cgstRate || 0,
                    "SGST %": l.sgstRate || 0,
                    "CGST Amt": l.cgstAmount || 0,
                    "SGST Amt": l.sgstAmount || 0,
                    "Line Total": l.totalAmount || 0,
                    "Lat": o.lat || "",
                    "Long": o.long || "",
                    "Status": o.status || "APPROVED"
                });
            });
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Detailed_DS_Orders");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `Detailed_DS_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportToExcel = () => {
        const data = orders.map(o => ({
            "Order ID": o.id,
            "Date": o.dateStr,
            "Distributor ID": o.distributorId || "N/A",
            "Distributor": o.distributorName || "N/A",
            "DS ID": o.dsId,
            "Outlet ID": o.outletId,
            "Outlet Name": o.outletName,
            "Beat": o.beat,
            "Total Qty": o.totalQty,
            "Basic Amount": (o.totalBasic || 0).toFixed(2),
            "CGST": (o.cgst || 0).toFixed(2),
            "SGST": (o.sgst || 0).toFixed(2),
            "Total Amount": (o.totalAmount || 0).toFixed(2),
            "Status": o.status || "APPROVED",
            "Latitude": o.lat || "N/A",
            "Longitude": o.long || "N/A",
            "Items Summary": Object.entries(o.items || {}).map(([sku, qty]) => `${sku}(${qty})`).join(", ")
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "DS_Orders");
        const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        saveAs(blob, `DS_Mobile_Orders_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <Layout>
            <div className="layout-content">
                {/* HEADER SECTION */}
                <div style={{ marginBottom: 40, display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                    <div>
                        <h1 style={{ fontSize: 32, marginBottom: 8, fontWeight: 900, letterSpacing: "-0.02em" }}>Field Deployment Logs</h1>
                        <p style={{ color: "var(--text-muted)", fontSize: 16 }}>Real-time intelligence on DS mobile orders, outlet performance, and location compliance.</p>
                    </div>
                    <div style={{ display: "flex", gap: 12 }}>
                        <button
                            onClick={exportToExcel}
                            className="btn-secondary"
                            style={{ display: "flex", alignItems: "center", gap: 8 }}
                        >
                            📊 Export Summary
                        </button>
                        <button
                            onClick={exportDetailedToExcel}
                            className="btn-primary"
                            style={{ display: "flex", alignItems: "center", gap: 8 }}
                        >
                            🔍 Detailed Manifest
                        </button>
                    </div>
                </div>

                <div className="premium-card" style={{ padding: 0, overflow: "hidden" }}>
                    <div style={{ padding: "16px 24px", borderBottom: "1px solid var(--border)", background: "var(--surface-hover)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 12, fontWeight: 800, color: "var(--text-main)" }}>DEPLOYMENT REGISTRY</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)" }}>{orders.length} TOTAL SESSIONS</span>
                    </div>
                    
                    <div style={{ maxHeight: 700, overflowY: "auto" }}>
                        <table className="enterprise-table">
                            <thead>
                                <tr>
                                    <th style={{ paddingLeft: 24 }}>Session Date</th>
                                    <th>Distributor Node</th>
                                    <th>Sales Executive</th>
                                    <th>Retail Outlet</th>
                                    <th>Beat Log</th>
                                    <th style={{ textAlign: "right" }}>Fiscal (Total)</th>
                                    <th style={{ textAlign: "center" }}>GPS Auth</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Scanning logistics records...</td>
                                    </tr>
                                ) : orders.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>No mobile orders detected in current audit cycle.</td>
                                    </tr>
                                ) : (
                                    orders.map(o => (
                                        <tr key={o.id}>
                                            <td style={{ paddingLeft: 24, fontSize: 12, color: "var(--text-muted)" }}>{o.dateStr}</td>
                                            <td>
                                                <div style={{ fontWeight: 700, fontSize: 13 }}>{o.distributorName}</div>
                                                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>ID: {o.distributorId}</div>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>{o.dsId}</td>
                                            <td>
                                                <div style={{ fontWeight: 800, color: "var(--primary)" }}>{o.outletName}</div>
                                                <div style={{ fontSize: 10, color: "var(--text-muted)" }}>ID: {o.outletId}</div>
                                            </td>
                                            <td>
                                                <span style={{ padding: "2px 8px", borderRadius: 6, background: "var(--surface-hover)", fontSize: 11, fontWeight: 700 }}>{o.beat}</span>
                                            </td>
                                            <td style={{ textAlign: "right", fontWeight: 800, color: "var(--success)" }}>
                                                ₹{(o.totalAmount || 0).toLocaleString()}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {o.lat && o.long ? (
                                                    <a
                                                        href={`https://www.google.com/maps?q=${o.lat},${o.long}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ 
                                                          display: "inline-flex", 
                                                          alignItems: "center", 
                                                          gap: 4, 
                                                          color: "var(--primary)", 
                                                          textDecoration: "none", 
                                                          fontSize: 12, 
                                                          fontWeight: 700,
                                                          background: "rgba(255, 107, 0, 0.1)",
                                                          padding: "4px 10px",
                                                          borderRadius: 8
                                                        }}
                                                    >
                                                        📍 MAP VIEW
                                                    </a>
                                                ) : (
                                                    <span style={{ fontSize: 11, color: "var(--text-muted)", opacity: 0.5 }}>- NO DATA -</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </Layout>
    );
}

export default DsOrderReport;

